import type { FlowState } from '@de/providers/dynamicExperienceReducer';
import type Lead from '@de/types/leadDelivery';
import buildLeadObject from '@de/utils/buildLeadObject';
import { transformFlowStateToLeadInputs } from '@de/utils/dataFromFlowUtils';

/**
 * Assemble an LDS lead object from a DE `flowState` snapshot for exposure
 * on the Configurable Experiences interpolation context as `context.lead`.
 *
 * This is the declarative-path analog of the legacy Supersonic results
 * page, which built the lead imperatively
 * (`transformFlowStateToLeadInputs(flowState)` → `buildLeadObject()`) and
 * handed it to the qualified-results orchestrator. The DSL/HEN path has no
 * orchestrator, so the lead is surfaced into `flowContext` and a result-page
 * config references it via `body: { lead: "{{context.lead}}" }` — the
 * Offerings `POST /v1/offerings/search/match` endpoint accepts a first-class
 * optional `lead` and forwards it to Delivery System `/v3/lead/qualify`.
 *
 * The lead is built from the answered questions only (each step's
 * `leadFieldAssociated` → value mapping). PII fields (first/last name, phone,
 * zip) are included only when they are captured as pre-results questions in
 * the flow; this helper does not reach into the governed lead-flow drawer.
 *
 * Returns `undefined` when no flow answer maps to a lead field. Without this
 * guard a flow with no mappable answers would still produce a truthy lead
 * (all-`undefined` fields plus the builder's hardcoded consent defaults),
 * forcing every `/match` call to carry a content-free lead. Skipping it keeps
 * the key absent until the flow actually captures lead data.
 *
 * Defensive by design: lead assembly must never break the surrounding
 * `flowContext` build. Any failure returns `undefined`, which degrades the
 * `/match` request to the no-lead path (qualification hop skipped) rather
 * than throwing during render.
 */
export function buildLeadContext(
  flowState: FlowState | undefined,
): Lead | undefined {
  if (!flowState) return undefined;
  try {
    const inputs = transformFlowStateToLeadInputs(flowState);
    if (Object.keys(inputs).length === 0) return undefined;
    return buildLeadObject(inputs);
  } catch {
    return undefined;
  }
}
